import React from 'react'
import { useNavigate } from 'react-router-dom'

interface TeamRef { id: string; name: string }
interface ProjectInfo { id: string; name: string; team_name: string }
interface PublicProjectInfo { id: string; name: string }
interface SharedProjectInfo { id: string; name: string }

// Sent to onSelectProject when the user clicks "Public" before its real project id
// is known (publicProjects is still empty because /projects/all hasn't resolved yet).
// The caller is expected to remember this as intent and resolve it once the real
// Public project loads, rather than treating it as a normal project id.
export const PENDING_PUBLIC_ID = '__pending_public__'

interface HomeSidebarProps {
  teams: TeamRef[]
  allProjects: ProjectInfo[]
  publicProjects: PublicProjectInfo[]
  sharedProjects: SharedProjectInfo[]
  selectedProject: string
  projectCounts: Record<string, number>
  teamContext: TeamRef | null
  onSelectTeam: (t: TeamRef | null) => void
  onSelectProject?: (projectId: string) => void
  userEmail?: string
  onSignOut: () => void
}

function initials(email?: string) {
  if (!email) return 'U'
  const name = email.split('@')[0]
  const parts = name.split(/[._-]/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.45)',
  textTransform: 'uppercase', padding: '0 12px',
}

export default function HomeSidebar({ teams, allProjects, publicProjects, sharedProjects, selectedProject, projectCounts, onSelectProject, userEmail, onSignOut }: HomeSidebarProps) {
  const navigate = useNavigate()

  // Teams become group headers directly, with their projects nested beneath — no separate workspace switcher.
  const groups = teams
    .map(team => ({
      team,
      items: allProjects.filter(p => p.team_name === team.name),
    }))
    .filter(g => g.items.length > 0)

  return (
    <div style={{
      width: 272, minWidth: 272, flex: '0 0 272px',
      background: 'var(--mitel-dark-blue)', display: 'flex', flexDirection: 'column',
      height: '100vh', position: 'sticky', top: 0, alignSelf: 'flex-start',
    }}>
      {/* Scrollable nav content — footer stays pinned below regardless of list length */}
      <div style={{
        flex: '1 1 auto', minHeight: 0, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', padding: '28px 14px', gap: 24,
      }}>
        {/* Brand block: logo image + Skilflow wordmark + BETA badge */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, padding: '0 10px' }}>
          <img src="/mitel-logo-white.svg" alt="Mitel" style={{ width: 96, height: 'auto', display: 'block' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.01em' }}>Skilflow</span>
            <span style={{
              fontSize: 7, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--mitel-dark-blue)',
              background: 'var(--mitel-yellow)', padding: '1px 4px', borderRadius: 3,
            }}>BETA</span>
          </div>
        </div>

        {/* New project */}
        <div
          onClick={() => navigate('/teams')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', cursor: 'pointer' }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="var(--mitel-yellow)" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.02em', color: 'var(--mitel-yellow)' }}>New project</span>
        </div>

        {/* Projects — Public is now wired to the real is_public=true project set
            (publicProjects prop), clickable like any other project, and its
            highlight reflects the real selectedProject instead of always-on.
            Unassigned is wired to the real onSelectProject('unassigned') affordance. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={sectionLabelStyle}>Projects</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {publicProjects.length === 0 && (
              // /projects/all hasn't resolved yet, so the real Public project id isn't
              // known — render a placeholder so a click in this ~1s window has something
              // to land on. onSelectProject receives PENDING_PUBLIC_ID instead of a real
              // id; DashboardPage stores that as intent and resolves it once loadGroups
              // returns the actual Public project, rather than silently dropping the click.
              <div
                onClick={() => onSelectProject?.(PENDING_PUBLIC_ID)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                  padding: '9px 12px', borderRadius: 4,
                  color: 'rgba(255,255,255,0.75)', fontWeight: 500, fontSize: 15, cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M3 12h18M12 3c2.5 2.5 3.8 5.5 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.5-3.8-9s1.3-6.5 3.8-9z" stroke="currentColor" strokeWidth="1.4" />
                  </svg>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Public</span>
                </div>
              </div>
            )}
            {publicProjects.map(p => {
              const active = selectedProject === p.id
              return (
                <div
                  key={p.id}
                  onClick={() => onSelectProject?.(p.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                    padding: '9px 12px', borderRadius: 4,
                    background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                    borderLeft: active ? '3px solid var(--mitel-yellow)' : '3px solid transparent',
                    color: active ? '#fff' : 'rgba(255,255,255,0.75)',
                    fontWeight: active ? 600 : 500, fontSize: 15,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = active ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = active ? 'rgba(255,255,255,0.08)' : 'transparent' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                      <path d="M3 12h18M12 3c2.5 2.5 3.8 5.5 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.5-3.8-9s1.3-6.5 3.8-9z" stroke="currentColor" strokeWidth="1.4" />
                    </svg>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                  </div>
                </div>
              )
            })}
            <div
              onClick={() => onSelectProject?.('unassigned')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                padding: '9px 12px', borderRadius: 4, color: 'rgba(255,255,255,0.75)', fontSize: 15, cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M4 11.5L12 4l8 7.5M6 10v9h12v-9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Unassigned
              </div>
            </div>
          </div>
        </div>

        {/* Shared with me — projects where the user has a project_members row but
            doesn't own the project. Rendered only once loaded; unlike Public there's
            no default-landing selection racing a click here, so no pending-id
            placeholder is needed. */}
        {sharedProjects.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={sectionLabelStyle}>Shared with me</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sharedProjects.map(p => {
                const active = selectedProject === p.id
                return (
                  <div
                    key={p.id}
                    onClick={() => onSelectProject?.(p.id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                      padding: '9px 12px', borderRadius: 4,
                      background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                      borderLeft: active ? '3px solid var(--mitel-yellow)' : '3px solid transparent',
                      color: active ? '#fff' : 'rgba(255,255,255,0.75)',
                      fontWeight: active ? 600 : 500, fontSize: 15,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = active ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = active ? 'rgba(255,255,255,0.08)' : 'transparent' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
                        <path d="M17 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="10" cy="7" r="3.2" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M19.5 20v-1.6a3.6 3.6 0 0 0-2.6-3.46M15 4.2a3.6 3.6 0 0 1 0 6.9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                      </svg>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Workspace groups — team name as section header, its projects nested beneath, real skill counts */}
        {groups.map(g => (
          <div key={g.team.id} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={sectionLabelStyle}>{g.team.name}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {g.items.map(p => (
                <div
                  key={p.id}
                  onClick={() => onSelectProject?.(p.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    padding: '8px 12px', borderRadius: 4, fontSize: 14, cursor: 'pointer',
                    color: 'rgba(255,255,255,0.75)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flex: '0 0 auto' }}>
                      <path d="M3 6h6l2 2h10v11H3V6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    </svg>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                  </div>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', flex: '0 0 auto' }}>{projectCounts[p.id] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Dashed secondary "New project" entry point, per prototype layout */}
        <div style={{ marginTop: 'auto', paddingTop: 14 }}>
          <div
            onClick={() => navigate('/teams')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 4,
              border: '1px dashed rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            New project
          </div>
        </div>
      </div>

      {/* Footer: user — pinned outside the scroll area so it's always visible */}
      <div style={{
        flex: '0 0 auto', padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.12)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%', background: 'var(--mitel-green)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
          color: '#fff', fontSize: 12, fontWeight: 700,
        }}>{initials(userEmail)}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {userEmail?.split('@')[0] || 'User'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            <span
              onClick={() => navigate('/settings')}
              style={{ color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#fff' }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)' }}
            >Settings</span>
            <span style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
            <span
              onClick={onSignOut}
              style={{ color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#fff' }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)' }}
            >Log out</span>
          </div>
        </div>
      </div>
    </div>
  )
}
